import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, Loader2, AlertCircle } from 'lucide-react'
import { listUsers, deleteUser, type YepUser } from '../../lib/api/users'
import { PageHead } from '../config/ui'
import { UserDrawer } from './UserDrawer'
import { cn } from '../../lib/cn'

export function UsersPage() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['operators'], queryFn: listUsers })
  const [target, setTarget] = useState<YepUser | 'new' | null>(null)

  const del = useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operators'] }),
  })

  const rows = q.data ?? []

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      <PageHead title="Operatori" description="Utenti che accedono al gestionale. Gestisci credenziali, stato e dati di ogni operatore.">
        <button
          onClick={() => setTarget('new')}
          className="inline-flex items-center gap-2 rounded-lg bg-verdigris px-4 py-[10px] text-[13.5px] font-semibold text-white hover:bg-verdigris-700"
        >
          <Plus className="h-4 w-4" /> Nuovo operatore
        </button>
      </PageHead>

      <div className="cv-panel overflow-hidden">
        {q.isError ? (
          <div className="px-4 py-16 text-center text-sm text-muted">
            <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare gli operatori.
          </div>
        ) : q.isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-faint" />
          </div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-16 text-center text-sm text-muted">Nessun operatore.</div>
        ) : (
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="border-b border-line-soft bg-paper/60 text-[11px] font-bold uppercase tracking-[1px] text-muted">
                <th className="px-5 py-3 text-left">Username</th>
                <th className="px-5 py-3 text-left">Nome</th>
                <th className="px-5 py-3 text-left">Email</th>
                <th className="px-5 py-3 text-left">Stato</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id ?? u.username} className="border-b border-line-soft last:border-0 hover:bg-paper/40">
                  <td className="px-5 py-3 font-mono text-coal">{u.username}</td>
                  <td className="px-5 py-3 text-coal">{u.name}</td>
                  <td className="px-5 py-3 text-slate">{u.email}</td>
                  <td className="px-5 py-3">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold',
                        u.enabled ? 'bg-ok/10 text-ok' : 'bg-line text-muted',
                      )}
                    >
                      <span className={cn('h-1.5 w-1.5 rounded-full', u.enabled ? 'bg-ok' : 'bg-faint')} />
                      {u.enabled ? 'Attivo' : 'Disabilitato'}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setTarget(u)} className="rounded-md p-1.5 text-slate hover:bg-paper hover:text-coal" title="Modifica">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (u.id && window.confirm(`Eliminare l'operatore "${u.username}"?`)) del.mutate(u.id)
                        }}
                        className="rounded-md p-1.5 text-slate hover:bg-venetian-soft hover:text-venetian"
                        title="Elimina"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <UserDrawer target={target} onClose={() => setTarget(null)} />
    </div>
  )
}
